"use client";

import {
  IconDotsVertical,
  IconLogout,
  IconMoon,
  IconNotification,
  IconSearch,
  IconSun,
} from "@tabler/icons-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useState } from "react";
import { useEffect } from "react";
import { useAppDispatch } from "utils/hooks";
import { ModalType } from "@/utils/modals/modalHelpers";
import { modalAction } from "@/ui-modules/modals/modalSlice";
import { LocalStorageItems } from "@/utils/hooks/localStorage";
import { serviceRequestAlerts } from "@/ui-modules/nav/navSlice";

export function NavUser({
  user,
  onLogout,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  onLogout?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { isMobile } = useSidebar();
  const prefersDarkMode = window.matchMedia(
    "(prefers-color-scheme: dark)",
  ).matches;

  const [isDarkMode, setDarkMode] = useState(
    localStorage.getItem(LocalStorageItems.DARKMODE)
      ? localStorage.getItem(LocalStorageItems.DARKMODE) === "true"
      : prefersDarkMode,
  );

  const toggleDarkMode = () => {
    setDarkMode(!isDarkMode);
  };
  const setAlertsTray = (value: boolean) => {
    dispatch(serviceRequestAlerts({ isOpen: value }));
  };
  const setScorecardModal = () => {
    dispatch(
      modalAction({ isOpen: true, modalName: ModalType.ScorecardSearch }),
    );
  };
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem(
      LocalStorageItems.DARKMODE,
      JSON.stringify(isDarkMode),
    );
  }, [isDarkMode]);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg bg-sidebar-accent text-sidebar-accent-foreground font-semibold">
                  {user.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
              <IconDotsVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback className="rounded-lg bg-muted text-muted-foreground font-semibold">
                      {user.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user.name}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => setScorecardModal()}
                className="cursor-pointer"
              >
                <IconSearch />
                Scorecard Lookup
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={toggleDarkMode}
                className="cursor-pointer"
              >
                {isDarkMode ? (
                  <>
                    <IconSun />
                    Light Mode
                  </>
                ) : (
                  <>
                    <IconMoon />
                    Dark Mode
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setAlertsTray(true)}
                className="cursor-pointer"
              >
                <IconNotification />
                Notifications
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="cursor-pointer">
              <IconLogout />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
